import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material";

import {
  CustomButton,
  CustomSelect,
  CustomTable,
  Text,
} from "../../shared/uiComponents";
import { useCheckPermission } from "../../shared/Helpers/hooks";
import { Container, Header } from "../../components/StyledComponents";
import SearchBar from "../../components/SearchBar";
import { AdminTypes } from "../../components/Menu";
import { PERMISSIONS } from "../../App/constants";
import { Headers, TableBody } from "./tableInfo";
import MobileView from "./mobileView";
import MobileViewBCBA from "./mobileViewBCBA";

import { DispatchProperties, useSelector } from "../../redux/store";
import { getClients } from "../../redux/State/clientSlice/clientSlice";
import {
  getCaseCoordinators,
  getUserClients,
} from "../../redux/State/clientSlice/userClientSlice";

const Clients = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<DispatchProperties>();
  const [params, setParams] = useSearchParams();

  const { permissionGranted } = useCheckPermission(PERMISSIONS.CLIENT.CREATE);

  const userId = useSelector((state) => state.account.user.id);
  const role = useSelector((state) => state.account.role);
  const clientsData = useSelector((state) => state.client.clients);
  const userClients = useSelector((state) => state.userClient.userClients);
  const loading = useSelector((state) => state.client.loading);
  const loadingUserClients = useSelector(
    (state) => state.userClient.loadingGetUserClients
  );
  const caseCoordinators = useSelector(
    (state) => state.userClient.caseCoordinators
  );

  const data = !!caseCoordinators
    ? caseCoordinators.map((x) => ({ id: x.id, name: x.fullName }))
    : [];

  const canHaveAction = role?.section.id === AdminTypes.admin || role?.section.id === AdminTypes.cs

  const addClientHandler = () => navigate("/clients/new");

  const onCaseCoordinatorChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    params.set("caseCoordinatorId", value);
    setParams(params);
  };

  useEffect(() => {
    dispatch(getCaseCoordinators());
  }, [dispatch]);

  useEffect(() => {
    const page = params.get("page") || "1";
    const pageSize = params.get("pageSize") || "8";
    const searchString = params.get("search") || "";
    const orderBy = params.get("orderBy") || "";
    const direction = params.get("direction") || "";
    const status = params.get("status");
    const caseCoordinatorId = params.get("caseCoordinatorId") || "";

    if (canHaveAction) {
      dispatch(
        getClients({
          page,
          pageSize,
          searchString,
          orderBy,
          direction,
          status,
          caseCoordinatorId,
        })
      );
      return;
    }

    dispatch(
      getUserClients({
        userId,
        page,
        pageSize,
        searchString,
        orderBy,
        direction,
        status,
      })
    );
  }, [params, userId, dispatch, canHaveAction]);

  return (
    <Container>
      <Header>
        <Text title={"Clients"} size="mediumBold" />
      </Header>
      <Header>
        {role?.section.id === AdminTypes.admin && (
          <div
            style={{ maxWidth: "400px", width: "100%", marginBottom: "24px" }}
          >
            <CustomSelect
              label="Case Coordinator: "
              setValue={onCaseCoordinatorChange}
              value={params.get("caseCoordinatorId") ?? ""}
              data={[{ id: "", name: "None" }, ...data]}
            />
          </div>
        )}
        <Header>
          {permissionGranted && (
            <CustomButton
              title={"Add client"}
              className={"marginRight16"}
              onClick={addClientHandler}
            />
          )}
          <SearchBar />
        </Header>
      </Header>

      <CustomTable
        headers={Headers}
        data={canHaveAction ? clientsData : userClients}
        TableBody={TableBody}
        loading={loading || loadingUserClients}
      />
      {canHaveAction ? (
        <MobileView {...clientsData} />
      ) : (
        <MobileViewBCBA {...userClients} />
      )}
    </Container>
  );
};

export default Clients;
